import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import AdminResourceCrudPage from './components/AdminResourceCrudPage';
import api from 'api/client';

const statusChip = (active) =>
  active ? (
    <Chip size="small" label="Active" sx={{ bgcolor: 'rgba(22,163,74,0.12)', color: '#166534', fontWeight: 700 }} />
  ) : (
    <Chip size="small" label="Inactive" sx={{ bgcolor: 'rgba(220,38,38,0.12)', color: '#991b1b', fontWeight: 700 }} />
  );

const sourceChip = (source) => (
  <Chip
    size="small"
    label={String(source || 'manual').toUpperCase()}
    sx={{ bgcolor: 'rgba(245,158,11,0.12)', color: '#92400e', fontWeight: 700 }}
  />
);

const parseError = (error, fallback = 'Request failed.') => {
  const payload = error?.response?.data;
  if (!payload) return error?.message || fallback;
  if (typeof payload === 'string') return payload;
  if (payload.detail) return payload.detail;
  return fallback;
};

const AdminNucleiTemplatesPage = () => {
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiResult, setAiResult] = useState('');
  const [zipOpen, setZipOpen] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipError, setZipError] = useState('');
  const [zipResult, setZipResult] = useState(null);
  const [zipForm, setZipForm] = useState({ file: null, overwrite: true, only_subpath: '' });
  const [remoteOpen, setRemoteOpen] = useState(false);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState('');
  const [remoteResult, setRemoteResult] = useState(null);
  const [remoteForm, setRemoteForm] = useState({ url: '', overwrite: true, only_subpath: '' });
  const [aiForm, setAiForm] = useState({
    prompt: '',
    name: '',
    template_id: '',
    severity: 'medium',
    tags: 'custom,web',
    save: true
  });

  const runGenerate = async () => {
    setAiLoading(true);
    setAiError('');
    try {
      const tags = aiForm.tags
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      const { data } = await api.post('/api/admin/nuclei-templates/generate_ai/', {
        prompt: aiForm.prompt,
        name: aiForm.name || undefined,
        template_id: aiForm.template_id || undefined,
        severity: aiForm.severity,
        tags,
        save: aiForm.save
      });
      setAiResult(data?.yaml_content || '');
      if (aiForm.save) {
        setRefreshNonce((v) => v + 1);
      }
    } catch (error) {
      setAiError(parseError(error, 'Failed to generate nuclei template.'));
    } finally {
      setAiLoading(false);
    }
  };

  const formatSummary = (result) => {
    if (!result) return '';
    return `Created: ${result.created || 0} • Updated: ${result.updated || 0} • Skipped: ${result.skipped || 0} • Failed: ${result.failed || 0} • Total: ${result.total || 0}`;
  };

  const runImportZip = async () => {
    if (!zipForm.file) return;
    setZipLoading(true);
    setZipError('');
    setZipResult(null);
    try {
      const form = new FormData();
      form.append('file', zipForm.file);
      form.append('overwrite', String(zipForm.overwrite));
      if (zipForm.only_subpath.trim()) form.append('only_subpath', zipForm.only_subpath.trim());
      const { data } = await api.post('/api/admin/nuclei-templates/import_archive/', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setZipResult(data);
      setRefreshNonce((v) => v + 1);
    } catch (error) {
      setZipError(parseError(error, 'Failed to import ZIP templates.'));
    } finally {
      setZipLoading(false);
    }
  };

  const runImportRemote = async () => {
    setRemoteLoading(true);
    setRemoteError('');
    setRemoteResult(null);
    try {
      const { data } = await api.post('/api/admin/nuclei-templates/import_remote/', {
        url: remoteForm.url.trim(),
        overwrite: remoteForm.overwrite,
        only_subpath: remoteForm.only_subpath.trim() || undefined
      });
      setRemoteResult(data);
      setRefreshNonce((v) => v + 1);
    } catch (error) {
      setRemoteError(parseError(error, 'Failed to import remote templates.'));
    } finally {
      setRemoteLoading(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          border: '1px solid rgba(245, 158, 11, 0.35)',
          borderRadius: 3,
          p: 2,
          bgcolor: 'var(--fz-surface-2, #fff)'
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" spacing={1.5}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Nuclei Template Studio
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create, version, enable, disable, and AI-generate custom Nuclei templates for future scans.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              sx={{
                bgcolor: '#111827',
                color: '#f59e0b',
                border: '1px solid #f59e0b',
                '&:hover': { bgcolor: '#0b1220', borderColor: '#f59e0b' }
              }}
              onClick={() => setZipOpen(true)}
            >
              Import ZIP
            </Button>
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              sx={{
                bgcolor: '#111827',
                color: '#f59e0b',
                border: '1px solid #f59e0b',
                '&:hover': { bgcolor: '#0b1220', borderColor: '#f59e0b' }
              }}
              onClick={() => setRemoteOpen(true)}
            >
              Import Remote
            </Button>
            <Button
              variant="contained"
              startIcon={<AutoFixHighIcon />}
              sx={{
                bgcolor: '#111827',
                color: '#f59e0b',
                border: '1px solid #f59e0b',
                '&:hover': { bgcolor: '#0b1220', borderColor: '#f59e0b' }
              }}
              onClick={() => setAiOpen(true)}
            >
              Generate with AI
            </Button>
          </Stack>
        </Stack>
      </Box>

      <AdminResourceCrudPage
        key={refreshNonce}
        title="Nuclei Templates"
        subtitle="Managed template registry used by Nuclei during web vulnerability scans."
        icon={TravelExploreIcon}
        endpoint="/api/admin/nuclei-templates/"
        loadParams={{ nonce: refreshNonce }}
        pageSize={100}
        pageSizeOptions={[50, 100, 250, 500]}
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'template_id', label: 'Template ID' },
          {
            key: 'severity',
            label: 'Severity',
            render: (row) => (
              <Chip
                size="small"
                label={String(row.severity || 'medium').toUpperCase()}
                sx={{ bgcolor: 'rgba(245,158,11,0.12)', color: '#92400e', fontWeight: 700 }}
              />
            )
          },
          { key: 'source', label: 'Source', render: (row) => sourceChip(row.source) },
          { key: 'is_active', label: 'Status', render: (row) => statusChip(row.is_active) },
          { key: 'usage_count', label: 'Uses', render: (row) => row.usage_count ?? 0 },
          {
            key: 'updated_at',
            label: 'Updated',
            render: (row) => (row.updated_at ? new Date(row.updated_at).toLocaleString() : '-')
          }
        ]}
        fields={[
          { name: 'name', label: 'Template Name', type: 'text', grid: 6 },
          { name: 'template_id', label: 'Template ID', type: 'text', grid: 6 },
          { name: 'description', label: 'Description', type: 'multiline', grid: 12 },
          {
            name: 'severity',
            label: 'Severity',
            type: 'select',
            grid: 4,
            options: [
              { value: 'info', label: 'Info' },
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
              { value: 'critical', label: 'Critical' }
            ]
          },
          { name: 'source', label: 'Source', type: 'select', grid: 4, options: [
            { value: 'manual', label: 'Manual' },
            { value: 'ai', label: 'AI' },
            { value: 'imported', label: 'Imported' }
          ] },
          { name: 'is_active', label: 'Active', type: 'switch', grid: 4, defaultValue: true },
          {
            name: 'tags',
            label: 'Tags (JSON array)',
            type: 'json',
            grid: 12,
            defaultJson: ['custom', 'web']
          },
          { name: 'yaml_content', label: 'Template YAML', type: 'multiline', grid: 12 }
        ]}
      />

      <Dialog open={aiOpen} onClose={() => (aiLoading ? null : setAiOpen(false))} maxWidth="md" fullWidth>
        <DialogTitle>Generate Nuclei Template with AI</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {aiError && <Alert severity="error">{aiError}</Alert>}
            <TextField
              label="What do you want to detect?"
              multiline
              minRows={3}
              value={aiForm.prompt}
              onChange={(e) => setAiForm((prev) => ({ ...prev, prompt: e.target.value }))}
              fullWidth
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                label="Template name"
                value={aiForm.name}
                onChange={(e) => setAiForm((prev) => ({ ...prev, name: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Template ID"
                value={aiForm.template_id}
                onChange={(e) => setAiForm((prev) => ({ ...prev, template_id: e.target.value }))}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                select
                label="Severity"
                value={aiForm.severity}
                onChange={(e) => setAiForm((prev) => ({ ...prev, severity: e.target.value }))}
                fullWidth
              >
                {['info', 'low', 'medium', 'high', 'critical'].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item.toUpperCase()}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Tags (comma-separated)"
                value={aiForm.tags}
                onChange={(e) => setAiForm((prev) => ({ ...prev, tags: e.target.value }))}
                fullWidth
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={aiForm.save}
                  onChange={(e) => setAiForm((prev) => ({ ...prev, save: e.target.checked }))}
                />
              }
              label="Save generated template immediately"
            />
            <TextField
              label="Generated YAML preview"
              multiline
              minRows={12}
              value={aiResult}
              onChange={(e) => setAiResult(e.target.value)}
              fullWidth
              InputProps={{ sx: { fontFamily: 'IBM Plex Mono, monospace', fontSize: '0.82rem' } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiOpen(false)} disabled={aiLoading}>
            Close
          </Button>
          <Button
            onClick={runGenerate}
            disabled={aiLoading || !aiForm.prompt.trim()}
            variant="contained"
            sx={{
              bgcolor: '#111827',
              color: '#f59e0b',
              border: '1px solid #f59e0b',
              '&:hover': { bgcolor: '#0b1220' }
            }}
          >
            {aiLoading ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={zipOpen} onClose={() => (zipLoading ? null : setZipOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>Import Nuclei Templates from ZIP</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {zipError && <Alert severity="error">{zipError}</Alert>}
            {zipResult && <Alert severity={zipResult.failed ? 'warning' : 'success'}>{formatSummary(zipResult)}</Alert>}
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
              {zipForm.file ? zipForm.file.name : 'Select ZIP archive'}
              <input
                hidden
                type="file"
                accept=".zip,application/zip"
                onChange={(e) => setZipForm((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
              />
            </Button>
            <TextField
              label="Only subpath inside ZIP (optional)"
              placeholder="nuclei-templates/http/cves"
              value={zipForm.only_subpath}
              onChange={(e) => setZipForm((prev) => ({ ...prev, only_subpath: e.target.value }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={zipForm.overwrite}
                  onChange={(e) => setZipForm((prev) => ({ ...prev, overwrite: e.target.checked }))}
                />
              }
              label="Overwrite existing template IDs"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZipOpen(false)} disabled={zipLoading}>
            Close
          </Button>
          <Button
            onClick={runImportZip}
            disabled={zipLoading || !zipForm.file}
            variant="contained"
            sx={{
              bgcolor: '#111827',
              color: '#f59e0b',
              border: '1px solid #f59e0b',
              '&:hover': { bgcolor: '#0b1220' }
            }}
          >
            {zipLoading ? 'Importing...' : 'Import ZIP'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={remoteOpen} onClose={() => (remoteLoading ? null : setRemoteOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>Import Nuclei Templates from Remote URL</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {remoteError && <Alert severity="error">{remoteError}</Alert>}
            {remoteResult && <Alert severity={remoteResult.failed ? 'warning' : 'success'}>{formatSummary(remoteResult)}</Alert>}
            <TextField
              label="Remote URL"
              placeholder="https://github.com/projectdiscovery/nuclei-templates"
              value={remoteForm.url}
              onChange={(e) => setRemoteForm((prev) => ({ ...prev, url: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Only subpath (optional)"
              placeholder="http/cves"
              value={remoteForm.only_subpath}
              onChange={(e) => setRemoteForm((prev) => ({ ...prev, only_subpath: e.target.value }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={remoteForm.overwrite}
                  onChange={(e) => setRemoteForm((prev) => ({ ...prev, overwrite: e.target.checked }))}
                />
              }
              label="Overwrite existing template IDs"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoteOpen(false)} disabled={remoteLoading}>
            Close
          </Button>
          <Button
            onClick={runImportRemote}
            disabled={remoteLoading || !remoteForm.url.trim()}
            variant="contained"
            sx={{
              bgcolor: '#111827',
              color: '#f59e0b',
              border: '1px solid #f59e0b',
              '&:hover': { bgcolor: '#0b1220' }
            }}
          >
            {remoteLoading ? 'Importing...' : 'Import Remote'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AdminNucleiTemplatesPage;
